// Build-time configuration (#1458). Every value comes from a FRONTEND_* env var
// baked in by CI. Analytics stays completely off unless the GTM container, the
// Cookiebot domain group and the GA4 measurement id are all configured: no
// consent banner, no tag. The measurement id is required because GA's opt-out
// (`ga-disable-<id>`) needs it; without it, withdrawal, logout or an excluded
// org could not stop the Google tag's automatic collection.

export interface AnalyticsConfig {
  gtmId: string;
  /** GTM environment query (`&gtm_auth=…&gtm_preview=…`), for non-prod containers. */
  gtmEnvQuery?: string;
  cookiebotCbid: string;
  /** GA4 measurement id, used for gtag session-ID lookup and the kill switch. */
  measurementId: string;
  /** `reed.ai` in prod, `<env>.reed.ai` elsewhere, so environments never share `_ga`. */
  cookieDomain?: string;
  /** Hosts never reported as referrers: the api-gateway and WorkOS AuthKit. */
  ignoredReferrerHosts: string[];
  /** The recruiter console tracks only once /auth/me says the org is eligible. */
  requireEligibility: boolean;
  /** Local development only: stand in for a Cookiebot decision. */
  devConsent?: 'granted' | 'denied';
}

export interface AnalyticsEnv {
  readonly FRONTEND_GTM_ID?: string;
  readonly FRONTEND_GTM_ENV_QUERY?: string;
  readonly FRONTEND_COOKIEBOT_CBID?: string;
  readonly FRONTEND_GA4_MEASUREMENT_ID?: string;
  readonly FRONTEND_GA_COOKIE_DOMAIN?: string;
  readonly FRONTEND_ANALYTICS_IGNORED_REFERRER_HOSTS?: string;
  readonly FRONTEND_ANALYTICS_DEV_CONSENT?: string;
  readonly FRONTEND_API_URL?: string;
  readonly DEV?: boolean;
}

const GTM_ID_RE = /^GTM-[A-Z0-9]{4,12}$/;
const CBID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MEASUREMENT_ID_RE = /^G-[A-Z0-9]{4,16}$/;
const GTM_ENV_QUERY_RE = /^(&[a-z_]+=[A-Za-z0-9_-]+)+$/;
const DOMAIN_RE = /^[a-z0-9.-]+\.[a-z]{2,}$/;

function hostOf(url: string | undefined): string | undefined {
  if (!url || !/^https?:\/\//.test(url)) {
    return undefined;
  }
  try {
    return new URL(url).host.toLowerCase();
  } catch {
    return undefined;
  }
}

/**
 * Builds the config from the app's import.meta.env, or returns null when
 * analytics is not configured for this build.
 */
export function configFromEnv(
  env: AnalyticsEnv,
  options: { requireEligibility: boolean },
): AnalyticsConfig | null {
  const gtmId = env.FRONTEND_GTM_ID?.trim() ?? '';
  const cookiebotCbid = env.FRONTEND_COOKIEBOT_CBID?.trim() ?? '';
  const measurementId = env.FRONTEND_GA4_MEASUREMENT_ID?.trim() ?? '';
  if (
    !GTM_ID_RE.test(gtmId) ||
    !CBID_RE.test(cookiebotCbid) ||
    !MEASUREMENT_ID_RE.test(measurementId)
  ) {
    return null;
  }

  const ignored = new Set(
    (env.FRONTEND_ANALYTICS_IGNORED_REFERRER_HOSTS ?? '')
      .split(',')
      .map((host) => host.trim().toLowerCase())
      .filter(Boolean),
  );
  const gatewayHost = hostOf(env.FRONTEND_API_URL);
  if (gatewayHost) {
    ignored.add(gatewayHost);
  }

  const config: AnalyticsConfig = {
    gtmId,
    cookiebotCbid,
    measurementId,
    ignoredReferrerHosts: [...ignored],
    requireEligibility: options.requireEligibility,
  };

  let envQuery = env.FRONTEND_GTM_ENV_QUERY?.trim() ?? '';
  if (envQuery && !envQuery.startsWith('&')) {
    envQuery = `&${envQuery}`;
  }
  if (GTM_ENV_QUERY_RE.test(envQuery)) {
    config.gtmEnvQuery = envQuery;
  }

  const cookieDomain = env.FRONTEND_GA_COOKIE_DOMAIN?.trim().toLowerCase() ?? '';
  if (DOMAIN_RE.test(cookieDomain)) {
    config.cookieDomain = cookieDomain;
  }

  const devConsent = env.FRONTEND_ANALYTICS_DEV_CONSENT?.trim();
  if (env.DEV === true && (devConsent === 'granted' || devConsent === 'denied')) {
    config.devConsent = devConsent;
  }
  return config;
}
